import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, runTransaction, serverTimestamp, Timestamp, where } from "firebase/firestore";
import { db, auth } from "@/firebase/config";
import type { DuelRepository } from "@/modules/leagues/repositories/DuelRepository";
import type { CreateDuelInput, Duel } from "@/modules/leagues/types/duel";

function toIso(value: unknown): string | null {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === "string") return value;
  return null;
}

function fromDoc(id: string, data: Record<string, unknown>): Duel {
  return {
    id,
    leagueId: data.leagueId as string,
    matchId: data.matchId as string,
    round: data.round as number,
    desafianteUid: data.desafianteUid as string,
    desafianteNome: data.desafianteNome as string,
    desafiantePalpite: data.desafiantePalpite as Duel["desafiantePalpite"],
    desafiadoUid: data.desafiadoUid as string,
    desafiadoNome: data.desafiadoNome as string,
    desafiadoPalpite: (data.desafiadoPalpite as Duel["desafiadoPalpite"]) ?? null,
    valorReais: data.valorReais as number,
    status: data.status as Duel["status"],
    matchKickoff: toIso(data.matchKickoff) ?? new Date(0).toISOString(),
    vencedorUid: (data.vencedorUid as string | null) ?? null,
    pontosDesafiante: (data.pontosDesafiante as number | null) ?? null,
    pontosDesafiado: (data.pontosDesafiado as number | null) ?? null,
    createdAt: toIso(data.createdAt) ?? new Date().toISOString(),
    respondedAt: toIso(data.respondedAt),
    resolvedAt: toIso(data.resolvedAt),
  };
}

/**
 * `groups/{leagueId}/duels/{duelId}`. Sem `or()` composto — duas queries
 * simples (desafiante / desafiado) mescladas no cliente, evita depender de
 * índice composto incerto pra uma condição OR entre dois campos diferentes.
 */
export class FirestoreDuelRepository implements DuelRepository {
  private requireUid(): string {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Você precisa estar autenticado para essa ação.");
    return uid;
  }

  private duelsCollection(leagueId: string) {
    return collection(db, "groups", leagueId, "duels");
  }

  async getMyDuels(leagueId: string): Promise<Duel[]> {
    const uid = this.requireUid();
    const [asChallenger, asChallenged] = await Promise.all([
      getDocs(query(this.duelsCollection(leagueId), where("desafianteUid", "==", uid))),
      getDocs(query(this.duelsCollection(leagueId), where("desafiadoUid", "==", uid))),
    ]);
    const byId = new Map<string, Duel>();
    for (const d of [...asChallenger.docs, ...asChallenged.docs]) {
      byId.set(d.id, fromDoc(d.id, d.data()));
    }
    return Array.from(byId.values()).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async createDuel(leagueId: string, input: CreateDuelInput): Promise<void> {
    const uid = this.requireUid();
    const nome = auth.currentUser?.displayName?.trim() || "Você";
    if (input.desafiadoUid === uid) throw new Error("Você não pode desafiar a si mesmo.");
    if (input.valorReais <= 0) throw new Error("O valor do duelo precisa ser maior que zero.");
    // Checagem no cliente é só uma primeira barreira de UX — a validação
    // que realmente importa é a Firestore Rule (server-side), que também
    // exige `matchKickoff > agora` na criação. Nunca confiar só nisto.
    if (new Date(input.matchKickoff).getTime() <= Date.now()) {
      throw new Error("Essa partida já começou — não é mais possível criar um duelo para ela.");
    }

    await addDoc(this.duelsCollection(leagueId), {
      leagueId,
      matchId: input.matchId,
      round: input.round,
      matchKickoff: Timestamp.fromDate(new Date(input.matchKickoff)),
      desafianteUid: uid,
      desafianteNome: nome,
      desafiantePalpite: input.desafiantePalpite,
      desafiadoUid: input.desafiadoUid,
      desafiadoNome: input.desafiadoNome,
      desafiadoPalpite: null,
      valorReais: input.valorReais,
      status: "pendente",
      vencedorUid: null,
      pontosDesafiante: null,
      pontosDesafiado: null,
      createdAt: serverTimestamp(),
      respondedAt: null,
      resolvedAt: null,
    });

    await this.writeFeedEntry(leagueId, "challenge_created", `${nome} desafiou ${input.desafiadoNome} num duelo 1x1.`, nome);
  }

  /** Feed (item pedido pelo usuário) — sempre melhor esforço, nunca deixa
   * uma falha aqui quebrar a ação principal do duelo (criar/aceitar). */
  private async writeFeedEntry(leagueId: string, type: string, descricao: string, administrador: string): Promise<void> {
    try {
      await addDoc(collection(db, "groups", leagueId, "history"), { type, descricao, administrador, createdAt: serverTimestamp() });
    } catch (err) {
      console.warn("[FirestoreDuelRepository] falha ao gravar entrada no feed (ação principal não foi afetada):", err);
    }
  }

  async acceptDuel(leagueId: string, duelId: string, palpite: { homeScore: number; awayScore: number }): Promise<void> {
    const uid = this.requireUid();
    const ref = doc(this.duelsCollection(leagueId), duelId);
    let desafianteNome = "";
    let desafiadoNome = "";
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Duelo não encontrado.");
      const data = snap.data();
      if (data.desafiadoUid !== uid) throw new Error("Este duelo não é seu para aceitar.");
      if (data.status !== "pendente") throw new Error("Este duelo já foi respondido.");
      // Revalida no servidor (não só no relógio da tela) — a partida pode
      // ter começado entre o usuário abrir a tela e clicar em Aceitar.
      const kickoff = data.matchKickoff instanceof Timestamp ? data.matchKickoff.toDate() : new Date(0);
      if (kickoff.getTime() <= Date.now()) {
        throw new Error("O prazo para aceitar esse duelo já encerrou — a partida começou.");
      }
      desafianteNome = data.desafianteNome as string;
      desafiadoNome = data.desafiadoNome as string;
      tx.update(ref, { status: "aceito", desafiadoPalpite: palpite, respondedAt: serverTimestamp() });
    });
    await this.writeFeedEntry(leagueId, "duel_accepted", `${desafiadoNome} aceitou o desafio de ${desafianteNome}.`, desafiadoNome);
  }

  async declineDuel(leagueId: string, duelId: string): Promise<void> {
    const uid = this.requireUid();
    const ref = doc(this.duelsCollection(leagueId), duelId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Duelo não encontrado.");
      const data = snap.data();
      if (data.desafiadoUid !== uid) throw new Error("Este duelo não é seu para recusar.");
      if (data.status !== "pendente") throw new Error("Este duelo já foi respondido.");
      tx.update(ref, { status: "recusado", respondedAt: serverTimestamp() });
    });
  }

  async cancelDuel(leagueId: string, duelId: string): Promise<void> {
    const uid = this.requireUid();
    const ref = doc(this.duelsCollection(leagueId), duelId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Duelo não encontrado.");
      const data = snap.data();
      if (data.desafianteUid !== uid) throw new Error("Este duelo não é seu para cancelar.");
      if (data.status !== "pendente") throw new Error("Só é possível cancelar um duelo ainda pendente.");
      tx.update(ref, { status: "cancelado", respondedAt: serverTimestamp() });
    });
  }

  private static readonly FINAL_STATUSES = ["finalizado", "empate", "recusado", "cancelado", "expirado"];

  async deleteDuel(leagueId: string, duelId: string): Promise<void> {
    const uid = this.requireUid();
    const ref = doc(this.duelsCollection(leagueId), duelId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return; // já não existe, nada a fazer
    const data = snap.data();
    if (data.desafianteUid !== uid && data.desafiadoUid !== uid) {
      throw new Error("Este duelo não é seu para excluir.");
    }
    if (!FirestoreDuelRepository.FINAL_STATUSES.includes(data.status)) {
      throw new Error("Só é possível excluir um duelo já finalizado.");
    }
    await deleteDoc(ref);
  }
}
